import { useCallback, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generatePageUrl } from '@/app/pages';
import { useQuery } from '@tanstack/react-query';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { UiButton, UiHStack, UiIconPencilSimple, UiStack, UiText } from '@/lib/ui';
import { InfoFormType, type InfoFormResponse, loadRegistrationInfoFormResponse, type InfoForm, infoFormResponseQueryKey, infoFormQueryKey, loadInfoForm } from '@/api/registration';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import InformationList from './InformationList';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import BaseButtonPlainIconEdit from '@/base/Button/PlainIconEdit';
import BaseButtonIconText from '@/base/Button/IconText';

const AdditionalInfoReview: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantCode, eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const registrationQuery = useRegistrationQuery(eventId);
  const registration = registrationQuery?.data;

  const additionalInfoQuery = useQuery<InfoFormResponse, Error>({
    queryKey: [infoFormResponseQueryKey, { registrationId: registration?.id, type: InfoFormType.ADDITIONAL }],
    queryFn: async () => {
      const response = await loadRegistrationInfoFormResponse(createTenantApiRequest)({
        registrationId: registration!.id,
        type: InfoFormType.ADDITIONAL
      });
      if (!response.item) {
        throw new Error('No data returned from loadVisitorInfoFormResponse');
      }
      return response.item;
    },
    enabled: !!registration && !isApiPreparing
  });

  const infoFormQuery = useQuery<InfoForm, Error>(
    [infoFormQueryKey, { id: additionalInfoQuery?.data?.id }],
    async () => {
      if (!additionalInfoQuery.data) {
        throw new Error('InfoForm is undefined');
      }
      const result = await loadInfoForm(createTenantApiRequest)(additionalInfoQuery.data.infoFormId);
      return result.item;
    },
    {
      enabled: !!additionalInfoQuery.data && !isApiPreparing
    }
  );

  const toModify = useCallback(
    () => {
      navigate(generatePageUrl(
        'RegistrationRegisterAdditionalInfo',
        { tenantCode, eventId },
        { ...Object.fromEntries(searchParams), review: true }
      ));
    },
    [eventId, navigate, searchParams, tenantCode]
  );

  return (
    <UiStack spacing={4}>
      <UiHStack spacing={4}>
        <UiText variant={'body1'} fontWeight={600}>
          Additional information
        </UiText>
        {/*<UiButton*/}
        {/*  rightIcon={(<UiIconPencilSimple color={'primary.500'} size={'xl'} weight={'bold'} />)}*/}
        {/*  variant={'ghost'}*/}
        {/*  colorScheme={'primary'}*/}
        {/*  fontSize={'md'}*/}
        {/*  // fontWeight={'bold'}*/}
        {/*  onClick={toModify}*/}
        {/*>*/}
        {/*  Modify*/}
        {/*</UiButton>*/}
        <BaseButtonIconText
          Icon={UiIconPencilSimple}
          onClick={toModify}
        >
          Edit
        </BaseButtonIconText>
        {/*<BaseButtonPlainIconEdit onClick={toModify}/>*/}
      </UiHStack>
      <QueryContainer query={infoFormQuery}>
        {(infoFormItem) => {
          return (
            <QueryContainer query={additionalInfoQuery}>
              {(additionalInfo) => {
                return additionalInfo && (
                  <InformationList
                    fieldsMetadata={infoFormItem.config.fieldsMetadata}
                    response={additionalInfo.response} />
                );
              }}
            </QueryContainer>
          );
        }}
      </QueryContainer>
    </UiStack>
  );
};

export default AdditionalInfoReview;
